import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import productImg from "../assets/productImg2.png";

const Hero = () => {
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const ctaRef = useRef(null);
  const imgRef = useRef(null);
  const bgTextRef = useRef(null);

  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: "power3.out" } });

    tl.fromTo(
      bgTextRef.current,
      { opacity: 0, scale: 1.08 },
      { opacity: 1, scale: 1, duration: 1.4 },
    )
      .fromTo(
        imgRef.current,
        { opacity: 0, y: 50, scale: 0.92 },
        { opacity: 1, y: 0, scale: 1, duration: 1.1 },
        "-=0.9",
      )
      .fromTo(
        titleRef.current,
        { opacity: 0, y: 24 },
        { opacity: 1, y: 0, duration: 0.9 },
        "-=0.6",
      )
      .fromTo(
        subtitleRef.current,
        { opacity: 0, y: 16 },
        { opacity: 1, y: 0, duration: 0.7 },
        "-=0.5",
      )
      .fromTo(
        ctaRef.current,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.6 },
        "-=0.4",
      );
  }, []);

  return (
    <Section>
      <BgText ref={bgTextRef}>STYLER</BgText>
      <Content>
        <Left>
          <Title ref={titleRef}>
            Flokë si prej salloni,
            <br />
            <em> kudo që je.</em>
          </Title>
          <Subtitle ref={subtitleRef}>
            Drejtuesi më portativ i flokëve në botë. Drejton flokët pa djegie.
            USB-C charging. Rezistent ndaj nxehtësisë. Gjithmonë i gatshëm.
          </Subtitle>
          <Cta ref={ctaRef}>
            <BuyBtn href="/checkout">Porosit Tani →</BuyBtn>
            <LearnBtn href="#features">Si Funksionon</LearnBtn>
          </Cta>
        </Left>

        <ImageWrap ref={imgRef} id="hero-product">
          <img src={productImg} alt="Siara Styler" draggable="false" />
          <Glow />
        </ImageWrap>
      </Content>

      <ScrollHint>
        <span />
        <p>Scroll</p>
      </ScrollHint>
    </Section>
  );
};

const Section = styled.section`
  position: relative;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
  padding: 0 3rem;
  background: linear-gradient(160deg, #fdf8f5 0%, #fceee8 50%, #f9e4da 100%);

  @media (max-width: 768px) {
    padding: 0 1.4rem;
  }
  @media (max-width: 900px) {
    min-height: 90vh;
  }
`;

const BgText = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-family: var(--serif);
  font-size: clamp(12rem, 25vw, 25rem);
  font-weight: 300;
  letter-spacing: 0.05em;
  color: rgba(212, 167, 154, 0.11);
  white-space: nowrap;
  pointer-events: none;
  user-select: none;
  z-index: 0;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2rem;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding-top: 5rem;

  @media (max-width: 900px) {
    flex-direction: column;
    text-align: center;
    padding-top: 6rem;
    gap: 1.5rem;
  }
`;

const Left = styled.div`
  flex: 1;
  max-width: 700px;
`;

const Title = styled.h1`
  font-family: var(--serif);
  font-size: clamp(7.6rem, 4.5vw, 4.4rem);
  font-weight: 300;
  line-height: 1.15;
  color: var(--dark);
  margin-bottom: 1.1rem;

  em {
    font-style: italic;
    color: var(--gold);
  }

  @media (max-width: 1150px) {
    font-size: 7vw;
  }
  @media (max-width: 900px) {
    font-size: 12vw;
  }
`;

const Subtitle = styled.p`
  font-family: var(--sans);
  font-size: clamp(0.85rem, 1.3vw, 0.98rem);
  font-weight: 300;
  color: var(--mid);
  line-height: 1.7;
  margin-bottom: 2rem;
  letter-spacing: 0.02em;
`;

const Cta = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

const BuyBtn = styled.a`
  font-family: var(--sans);
  font-size: 0.78rem;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--dark);
  padding: 0.9rem 2.2rem;
  border-radius: 100px;
  transition:
    background 0.3s ease,
    transform 0.2s ease;

  &:hover {
    background: var(--gold);
    transform: translateY(-2px);
  }
`;

const LearnBtn = styled.a`
  font-family: var(--sans);
  font-size: 0.78rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--mid);
  background: transparent;
  padding: 0.9rem 1.8rem;
  border-radius: 100px;
  border: 1px solid var(--rose);
  transition:
    border-color 0.3s ease,
    color 0.3s ease,
    transform 0.2s ease;

  &:hover {
    border-color: var(--dusty);
    color: var(--dark);
    transform: translateY(-2px);
  }
`;

const ImageWrap = styled.div`
  flex: 1;
  max-width: 460px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: 100%;
    max-width: 420px;
    object-fit: contain;
    filter: drop-shadow(0 32px 64px rgba(180, 120, 100, 0.22));
    position: relative;
    z-index: 1;
  }
  @media (max-width: 1150px) {
    max-width: 40%;
  }

  @media (max-width: 900px) {
    max-width: 40%;
  }
`;

const Glow = styled.div`
  position: absolute;
  width: 300px;
  height: 300px;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(232, 197, 184, 0.5) 0%,
    transparent 70%
  );
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
`;

const ScrollHint = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  z-index: 1;

  span {
    width: 1px;
    height: 34px;
    background: linear-gradient(to bottom, transparent, var(--dusty));
    animation: scrollPulse 1.8s ease-in-out infinite;
  }

  p {
    font-family: var(--sans);
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--dusty);
  }

  @keyframes scrollPulse {
    0%,
    100% {
      opacity: 0.3;
      transform: scaleY(0.8);
    }
    50% {
      opacity: 1;
      transform: scaleY(1);
    }
  }
`;

export default Hero;
